import { cardSubtle } from "@/lib/card-styles";
import { cn } from "@/lib/utils";
import type { RemapCandidateGroup } from "@/lib/kb-remap-groups";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const UNASSIGN_VALUE = "__unassign__";

interface UnmatchedColumnRemapProps {
  unmatchedColumns: string[];
  /** `null` while the KB catalog is still loading or after it failed to load —
   *  degrades to the pre-existing read-only list instead of blocking the import. */
  candidateGroups: RemapCandidateGroup[] | null;
  /** Canonical ids already claimed by a selected matched column or another
   *  remapped column — shown as disabled so the conflict stays visible. */
  claimedIds: ReadonlySet<string>;
  /** csv_column → canonical_id for columns the operator has assigned so far. */
  assignments: Readonly<Record<string, string>>;
  onAssign: (csvColumn: string, canonicalId: string | null) => void;
}

export function UnmatchedColumnRemap({
  unmatchedColumns,
  candidateGroups,
  claimedIds,
  assignments,
  onAssign,
}: UnmatchedColumnRemapProps) {
  if (unmatchedColumns.length === 0) return null;

  if (candidateGroups === null) {
    return (
      <div>
        {/* An unmapped column must look different from an absent one. */}
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
          Ohitetut sarakkeet ({unmatchedColumns.length})
        </div>
        <p className="font-mono text-[10px] text-muted-foreground/70">
          {unmatchedColumns.join(", ")}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
        Ohitetut sarakkeet — liitä KB-signaaliin ({unmatchedColumns.length})
      </div>
      <div className="space-y-1.5">
        {unmatchedColumns.map((csvColumn) => {
          const assignedId = assignments[csvColumn];
          return (
            <div
              key={csvColumn}
              className={cn(
                cardSubtle,
                "flex items-center gap-2 px-2.5 py-1.5 text-[11px]",
              )}
            >
              <span className="min-w-0 flex-1 truncate font-mono text-foreground">
                {csvColumn}
              </span>
              <span className="shrink-0 text-muted-foreground">→</span>
              <Select
                value={assignedId ?? ""}
                onValueChange={(v) =>
                  onAssign(csvColumn, v === UNASSIGN_VALUE ? null : v)
                }
              >
                <SelectTrigger className="h-7 min-w-0 flex-1 truncate text-[11px]">
                  <SelectValue placeholder="Valitse signaali…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNASSIGN_VALUE}>— Ei liitetä —</SelectItem>
                  {candidateGroups.map((group) => (
                    <SelectGroup key={group.label ?? "__flat__"}>
                      {group.label && <SelectLabel>{group.label}</SelectLabel>}
                      {group.dataPoints.map((dp) => {
                        const claimedByOther =
                          claimedIds.has(dp.id) && assignedId !== dp.id;
                        return (
                          <SelectItem
                            key={dp.id}
                            value={dp.id}
                            disabled={claimedByOther}
                          >
                            {dp.name_fi}
                            {claimedByOther ? " (varattu)" : ""}
                          </SelectItem>
                        );
                      })}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        })}
      </div>
    </div>
  );
}
